import { useCallback, useEffect, useRef, useState } from 'react';
import { Activity } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Skeleton } from '@/components/ui/skeleton';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { prixiaService } from '../services/prixia-service';
import type {
  AjustesDeLecturas,
  FilaDeLecturaPrixia,
  FiltroDeEstadoDeLectura,
  ResumenDeLecturas,
} from '../types/prixia.types';
import { TablaDeLecturasPrixia } from '../components/tabla-de-lecturas-prixia';

function Cifra({ etiqueta, valor }: { etiqueta: string; valor: number | string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-medium text-muted-foreground">{etiqueta}</p>
        <p aria-label={etiqueta} className="text-2xl font-bold tabular-nums">
          {valor}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Los ajustes de lecturas que la plataforma cambia sin tocar código (plan 297, P5-P6).
 * El tope de lecturas simultáneas se muestra y no se edita: lo fija el servidor.
 */
type CampoDeAjuste = 'leerAhoraPorDia' | 'minimoChequeoHoras' | 'minimoBarridoHoras';

const NOMBRE_DEL_AJUSTE: Record<CampoDeAjuste, { nombre: string; unidad: string }> = {
  leerAhoraPorDia: { nombre: 'Lecturas a pedido por empresa, por día', unidad: '' },
  minimoChequeoHoras: {
    nombre: 'Una empresa puede programar el chequeo no más seguido que cada',
    unidad: ' horas',
  },
  minimoBarridoHoras: {
    nombre: 'Una empresa puede programar el barrido no más seguido que cada',
    unidad: ' horas',
  },
};

function AjustesDeLecturasCard() {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [ajustes, setAjustes] = useState<AjustesDeLecturas | null>(null);
  const [form, setForm] = useState({
    leerAhoraPorDia: '',
    minimoChequeoHoras: '',
    minimoBarridoHoras: '',
  });
  const [guardando, setGuardando] = useState(false);
  const [sinAjustes, setSinAjustes] = useState(false);

  const aplicar = useCallback((a: AjustesDeLecturas) => {
    setAjustes(a);
    setForm({
      leerAhoraPorDia: String(a.leerAhoraPorDia),
      minimoChequeoHoras: String(a.minimoChequeoHoras),
      minimoBarridoHoras: String(a.minimoBarridoHoras),
    });
  }, []);

  const cargarAjustes = useCallback(() => {
    setSinAjustes(false);
    prixiaService
      .ajustes()
      .then(aplicar)
      .catch(() => {
        setAjustes(null);
        setSinAjustes(true);
      });
  }, [aplicar]);

  useEffect(() => {
    cargarAjustes();
  }, [cargarAjustes]);

  const enviar = async (cambio: Partial<Record<CampoDeAjuste, number>>) => {
    setGuardando(true);
    try {
      aplicar(await prixiaService.guardarAjustes(cambio));
      toast({ title: 'Ajustes guardados', description: 'Rigen desde ya para todas las empresas.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'No se pudieron guardar',
        description: mensajeDeError(err, 'Revisá los números.'),
      });
    } finally {
      setGuardando(false);
    }
  };

  /**
   * Manda sólo lo que cambió (no pisa con valores viejos lo que otro cambió). Un campo
   * vacío no se manda como 0: pide completarlo. Dejar el cupo en 0 o bajar un mínimo
   * pregunta antes, porque rige para todas las empresas.
   */
  const guardar = () => {
    if (!ajustes) return;
    const campos = Object.keys(NOMBRE_DEL_AJUSTE) as CampoDeAjuste[];
    const valores = campos.map((c) => ({ campo: c, texto: form[c].trim() }));
    if (valores.some(({ texto }) => texto === '' || !Number.isInteger(Number(texto)))) {
      toast({
        variant: 'destructive',
        title: 'Completá los tres números',
        description: 'Cada ajuste tiene que ser un número entero.',
      });
      return;
    }
    const cambio: Partial<Record<CampoDeAjuste, number>> = {};
    for (const { campo, texto } of valores) {
      if (Number(texto) !== ajustes[campo]) cambio[campo] = Number(texto);
    }
    const cambiados = Object.keys(cambio) as CampoDeAjuste[];
    if (cambiados.length === 0) {
      toast({ title: 'No hay cambios para guardar' });
      return;
    }
    const cupoEnCero = cambio.leerAhoraPorDia === 0;
    const bajaUnMinimo =
      (cambio.minimoChequeoHoras ?? Infinity) < ajustes.minimoChequeoHoras ||
      (cambio.minimoBarridoHoras ?? Infinity) < ajustes.minimoBarridoHoras;
    if (!cupoEnCero && !bajaUnMinimo) {
      void enviar(cambio);
      return;
    }
    const lista = cambiados
      .map((c) => {
        const { nombre, unidad } = NOMBRE_DEL_AJUSTE[c];
        return `${nombre}: ${ajustes[c]} → ${cambio[c]}${unidad}.`;
      })
      .join(' ');
    confirm({
      title: '¿Cambiar los ajustes para todas las empresas?',
      description: `${lista}${
        cupoEnCero ? ' Ninguna empresa sin cupo propio podrá pedir lecturas nuevas.' : ''
      }`,
      confirmText: 'Sí, cambiar',
      onConfirm: () => {
        void enviar(cambio);
      },
    });
  };

  return (
    <Card>
      <ConfirmDialog />
      <CardHeader>
        <CardTitle className="text-lg">Ajustes de las lecturas</CardTitle>
        <p className="text-sm text-muted-foreground">
          Valen para todas las empresas. El cupo de una empresa en particular se cambia desde su
          ficha («Tiendas disponibles»).
        </p>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        {sinAjustes && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 md:col-span-3">
            <p className="text-sm text-destructive">No se pudieron leer los ajustes.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={cargarAjustes}
              aria-label="Reintentar leer los ajustes"
            >
              Reintentar
            </Button>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="ajuste-cupo">Lecturas a pedido por empresa, por día</Label>
          <Input
            id="ajuste-cupo"
            type="number"
            min={0}
            max={100}
            value={form.leerAhoraPorDia}
            onChange={(e) => setForm({ ...form, leerAhoraPorDia: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Sumarse a una lectura que ya está en curso o en cola no gasta cupo. 0 = nadie puede
            pedir lecturas nuevas.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ajuste-chequeo">
            Una empresa puede programar el chequeo no más seguido que cada (horas)
          </Label>
          <Input
            id="ajuste-chequeo"
            type="number"
            min={1}
            value={form.minimoChequeoHoras}
            onChange={(e) => setForm({ ...form, minimoChequeoHoras: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ajuste-barrido">
            Una empresa puede programar el barrido no más seguido que cada (horas)
          </Label>
          <Input
            id="ajuste-barrido"
            type="number"
            min={1}
            value={form.minimoBarridoHoras}
            onChange={(e) => setForm({ ...form, minimoBarridoHoras: e.target.value })}
          />
        </div>
        <p className="text-sm text-muted-foreground md:col-span-3">
          {`Lecturas al mismo tiempo en toda la plataforma: ${ajustes?.lecturasSimultaneas ?? '—'} (lo fija el servidor).`}
        </p>
        {/* A la izquierda (abajo de los campos): contra el borde derecho quedaba
            debajo de los botones flotantes de ayuda, reportes y asistente. */}
        <div className="flex md:col-span-3">
          <Button onClick={guardar} disabled={guardando || !ajustes}>
            {guardando ? 'Guardando…' : 'Guardar ajustes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Todas las lecturas del catálogo de Prixia (plan 297): las del calendario, las de la
 * plataforma y las que pidió cada empresa. Es el único lugar donde se ve quién pidió
 * cada una (C10).
 */
export function AdminLecturasPrixiaPage() {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [cancelando, setCancelando] = useState<string | null>(null);
  const [estado, setEstado] = useState<FiltroDeEstadoDeLectura>('TODAS');
  const [dias, setDias] = useState(7);
  const [datos, setDatos] = useState<{
    resumen: ResumenDeLecturas;
    lecturas: FilaDeLecturaPrixia[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Cada pedido lleva su número: si se cambia el filtro antes de que llegue la
  // respuesta anterior, la vieja se ignora (no pisa la del filtro nuevo).
  const pedido = useRef(0);

  const cargar = useCallback(async () => {
    const este = ++pedido.current;
    try {
      const r = await prixiaService.lecturas({ estado, dias });
      if (este !== pedido.current) return;
      setDatos(r);
      setError(null);
    } catch (err) {
      if (este !== pedido.current) return;
      setError(mensajeDeError(err, 'No se pudieron leer las lecturas.'));
    }
  }, [estado, dias]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cancelar = (l: FilaDeLecturaPrixia) => {
    const hacer = async () => {
      setCancelando(l.id);
      try {
        await prixiaService.cancelarLectura(l.id);
        toast({ title: 'Lectura cancelada' });
        await cargar();
      } catch (err) {
        toast({
          variant: 'destructive',
          title: 'No se pudo cancelar',
          description: mensajeDeError(err, 'Intentá de nuevo.'),
        });
      } finally {
        setCancelando(null);
      }
    };
    if (l.status !== 'RUNNING') {
      void hacer();
      return;
    }
    confirm({
      title: '¿Cancelar la lectura?',
      description:
        'La lectura ya está leyendo para todas las empresas que tienen la tienda; lo leído hasta ahora no se reparte.',
      confirmText: 'Sí, cancelar',
      variant: 'destructive',
      onConfirm: () => {
        void hacer();
      },
    });
  };

  return (
    <AppLayout variante="plataforma">
      <ConfirmDialog />
      {/* pb-32: aire abajo para subir lo último por encima de los botones flotantes. */}
      <div className="space-y-6 p-6 pb-32">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Activity className="h-6 w-6" aria-hidden="true" />
            Lecturas de Prixia
          </h1>
          <p className="text-sm text-muted-foreground">
            Todas las lecturas del catálogo: las del calendario, las que pidió la plataforma y las
            que pidió cada empresa. Sólo acá se ve quién pidió cada una.
          </p>
        </div>

        {error && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <p className="text-sm text-destructive">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void cargar()}
              aria-label="Reintentar leer las lecturas"
            >
              Reintentar
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Cifra etiqueta="Leyendo ahora" valor={datos?.resumen.enCurso ?? '—'} />
          <Cifra etiqueta="En cola" valor={datos?.resumen.enCola ?? '—'} />
          <Cifra etiqueta="Fallidas en 24 h" valor={datos?.resumen.fallidas24h ?? '—'} />
          <Cifra etiqueta="Terminadas en 24 h" valor={datos?.resumen.completadas24h ?? '—'} />
          <Cifra
            etiqueta="Al mismo tiempo, como máximo"
            valor={datos?.resumen.simultaneas ?? '—'}
          />
        </div>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-lg">Lecturas</CardTitle>
            <div className="flex gap-2">
              <SelectNativo
                aria-label="Estado de la lectura"
                value={estado}
                onChange={(e) => setEstado(e.target.value as FiltroDeEstadoDeLectura)}
              >
                <option value="TODAS">Todas</option>
                <option value="EN_CURSO">Leyendo</option>
                <option value="EN_COLA">En cola</option>
                <option value="FALLIDAS">Fallidas</option>
                <option value="COMPLETADAS">Terminadas</option>
              </SelectNativo>
              <SelectNativo
                aria-label="Período"
                value={String(dias)}
                onChange={(e) => setDias(Number(e.target.value))}
              >
                <option value="1">Último día</option>
                <option value="7">Últimos 7 días</option>
                <option value="30">Últimos 30 días</option>
              </SelectNativo>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {datos || error ? (
              <TablaDeLecturasPrixia
                lecturas={datos?.lecturas ?? []}
                mostrarTienda
                onCancelar={cancelar}
                ocupado={cancelando !== null}
                vacio={datos ? undefined : 'No hay lecturas para mostrar.'}
              />
            ) : (
              <div className="p-4">
                <Skeleton className="h-24 w-full" />
              </div>
            )}
          </CardContent>
        </Card>

        <AjustesDeLecturasCard />
      </div>
    </AppLayout>
  );
}

export default AdminLecturasPrixiaPage;
